import Vue from 'vue'
import accounting from 'accounting'
import moment from 'moment'
import vueFilterPrettyBytes from 'vue-filter-pretty-bytes'
import getSymbolFromCurrency from 'currency-symbol-map'


Vue.use(vueFilterPrettyBytes)

accounting.settings.currency.precision = 2;

Vue.filter('money', (v, currency, precision) => (v === null || v === undefined) ? '' : accounting.formatMoney(v, getSymbolFromCurrency(currency || 'USD'), precision == null ? 2 : precision) )
Vue.filter('money_short', (v, currency, precision) => {
    if (v === null || v === undefined) return '';

    const symbol = getSymbolFromCurrency(currency || 'USD');

    if (v < 100 && (!Number.isInteger(v)))
        return accounting.formatMoney(v, symbol, 2);

    return accounting.formatMoney(v, symbol, 0);
});

/**
 * Shorten a figure to K / M, for tiles and axes where the magnitude is the
 * point and the exact digits are noise.
 *
 * One decimal below ten of a unit (3.2K, 1.4M) and none above it (12K, 24M),
 * so a column of these keeps a stable width.
 *
 * Round ONCE, at the precision actually shown, and hand `render` that exact
 * number. Rounding to one decimal and letting the formatter round again
 * carries twice - 999,499 came out as "1,000K" that way.
 *
 * 9.95 rather than 10 is the precision cut-off because 9.95 shows as 10.0, and
 * "10.0K" beside "12K" stops the column lining up.
 *
 * @param n        the value
 * @param render   (shown, precision) => string - where the currency symbol,
 *                 if any, gets applied
 */
const compact = (n, render) => {
    const abs = Math.abs(n)
    // sign first: accounting would render -$4.5K as $-4.5K
    const sign = n < 0 ? '-' : ''

    const scaled = (value, suffix, next = null) => {
        const precision = value >= 9.95 ? 0 : 1
        const shown = Number(value.toFixed(precision))

        // a genuine carry into the next unit: 999,999 is "1.0M", not "1,000K"
        if (shown >= 1000 && next)
            return next()

        return sign + render(shown, precision) + suffix
    }

    if (abs >= 1e6)
        return scaled(abs / 1e6, 'M')

    if (abs >= 1e3)
        return scaled(abs / 1e3, 'K', () => scaled(abs / 1e6, 'M'))

    return sign + render(abs, 0)
}

const finite = (v) => {
    if (v === null || v === undefined || v === '') return null

    const n = Number(v)

    return isFinite(n) ? n : null
}

/** Money at a glance - $3.2K, $1.4M. money_short keeps every dollar; this does not. */
Vue.filter('money_compact', (v, currency) => {
    const n = finite(v)
    if (n === null) return ''

    const symbol = getSymbolFromCurrency(currency || 'USD')

    return compact(n, (shown, precision) => accounting.formatMoney(shown, symbol, precision))
})

/** A count at a glance - 12K, 1.2M. For tallies that run long, like lifetime totals. */
Vue.filter('number_compact', (v) => {
    const n = finite(v)
    if (n === null) return ''

    return compact(n, (shown, precision) => accounting.formatNumber(shown, precision))
})
Vue.filter('decimal', (v, precision) => accounting.format(v, precision == null ? 2 : precision))
Vue.filter('timediff', (v) => {
    if (!v)
        return null

    const diff = moment(v).fromNow(true)
    if (moment().diff(v, 'seconds') >= 0)
        return diff + " ago"
    else
        return "in " + diff
})


Vue.filter('hours',  (decimalHours) => {
    if (decimalHours >= 24) {
        const days = Math.floor(decimalHours / 24);
        return `${days} day${days !== 1 ? 's' : ''}`;
    } else {
        const roundedHours = Math.round(decimalHours * 10) / 10;
        return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
    }
})

Vue.filter('trim', (v) => v.trim().replace(/^\s\n+|\s\n+$/g, ''))
Vue.filter('state', (v) => v ? false : null)
Vue.filter('date', (v) => v && new Date(v).toLocaleString())
Vue.filter('dateonlytz', (isoDate, timeZone) => {
    const date = new Date(isoDate); // Force midnight UTC to preserve day

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const dateString = parts
        .filter(p => p.type !== 'literal' && p.type !== 'timeZoneName')
        .map(p => p.value)
        .join(' ');

    const tzAbbrev = parts.find(p => p.type === 'timeZoneName')?.value?.match(/\b([A-Z]{2,5})\b/)?.[1];

    return `${dateString} ${tzAbbrev || ''}`.trim();
})
Vue.filter('datetz', (isoDate, timeZone) => {
    const date = new Date(isoDate);

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
        hour12: false,
    });

    // This gives something like: "May 20, 2025, 18:30:00 PDT"
    const parts = formatter.formatToParts(date);
    const formatted = parts.map(p => p.value).join('');
    const tzAbbrev = parts.find(p => p.type === 'timeZoneName')?.value?.match(/\b([A-Z]{2,5})\b/)?.[1];

    return formatted.replace(/\s*[A-Z]{2,5}$/, '') + (tzAbbrev ? ` ${tzAbbrev}` : '');
})

Vue.filter('dateonly', (v) => v && new Date(v).toLocaleDateString())
Vue.filter('ucfirst', (string) => string.charAt(0).toUpperCase() + string.slice(1))
Vue.filter('s', (list) => {
    if (!list || list.length == 0)
        return 's'

    return list.length > 1 ? 's' : ''
})